import { useState, useEffect } from "react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Plus, Trash, Save, FileText, Check, ChevronsUpDown } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Label } from "../../../components/ui/label"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover"
import { useToast } from "@/view/hooks"
import type { ResponseExerciseSheet } from "./response-exercise-sheet"
import { useSearchParams } from "react-router-dom"
import { convertParamsToJson, useCashfitParams } from "@/view/hooks/use-params"
import { useLocalStorage } from "@/remote/local-storage"
import { useRemoteClientGet, useRemoteClientPost, useRemoteClientPut } from "@/configs/http-client"
import { CatchMessageError } from "@/remote/catch-message-error"
import { cn } from '@/lib/utils'

type ExerciseType = "KG" | "PESO" | "LIBRA" | "PERCENTUAL"

interface Exercise {
  id?: number
  repeticoes: number
  serie: number
  tipo: ExerciseType
  carga: number
  velocidadeExecucao?: string
  tempoPausaSeries?: number
  observacao?: string
  exercicio: {
    id: number
    nome?: string
  }
}

interface Params {
  changeTab: (data: string) => void
}

export function AddTraining({ changeTab }: Params) {
  const [params] = useSearchParams()
  const { getParam, setParams } = useCashfitParams(convertParamsToJson(params))
  const { getUnitId } = useLocalStorage()
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  const { remoteClientGet } = useRemoteClientGet()
  const sheetId = getParam("sheetId")
  const trainingId = getParam("traningId")

  const [isEditMode, setIsEditMode] = useState(false)
  const { showToast } = useToast()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ResponseExerciseSheet | null>(null)

  const [trainingName, setTrainingName] = useState("")
  const [exercises, setExercises] = useState<Array<Exercise>>([])
  const [availableExercises, setAvailableExercises] = useState<Array<{ id: number; nome: string }>>([])
  const [submitting, setSubmitting] = useState(false)

  // Estados para controlar os popovers de pesquisa de exercícios
  const [openPopovers, setOpenPopovers] = useState<{ [key: number]: boolean }>({})
  const [searchValues, setSearchValues] = useState<{ [key: number]: string }>({})

  const trainingSheet = getParam("sheetId" as any)

  useEffect(() => {
    if (!loading) {
      remoteClientGet("/fichas/" + trainingSheet, {
        idUnidade: getUnitId(),
      })
        .then((response) => setData(response.data.data))
        .catch(CatchMessageError)
        .finally(() => setLoading(false))

      httpClientPost("/grupos/filtro", {
        idUnidade: getUnitId(),
      })
        .then(console.debug)
        .catch(CatchMessageError)

      httpClientPost("/exercicios/filtro?page=0&size=1000", {
        idUnidade: getUnitId(),
      })
        .then((response) => {
          if (response.data && Array.isArray(response.data.data.content)) {
            setAvailableExercises(response.data.data.content.map((e: any) => ({ ...e, nome: e.nome.toUpperCase() })))
          } else {
            console.error("Unexpected response format for exercises:", response.data)
            setAvailableExercises([])
          }
        })
        .catch((error) => {
          console.error("Error fetching exercises:", error)
          CatchMessageError(error)
          setAvailableExercises([])
        })
    }
  }, [params])

  useEffect(() => {
    if (trainingId) {
      setIsEditMode(true)
      remoteClientGet(`/fichas-treino/${trainingId}`, {
        idUnidade: getUnitId(),
      })
        .then((response) => {
          const trainingData = response.data.data
          setTrainingName(trainingData.nome)
          setExercises(trainingData.exercicios)
        })
        .catch(CatchMessageError)
    } else {
      setIsEditMode(false)
      setTrainingName("")
      setExercises([])
    }
  }, [trainingId])

  // Função para filtrar exercícios baseado na pesquisa
  const getFilteredExercises = (searchTerm: string) => {
    if (!searchTerm) return availableExercises
    return availableExercises.filter((exercise) => exercise.nome.toLowerCase().includes(searchTerm.toLowerCase()))
  }

  const addExercise = () => {
    const newIndex = exercises.length
    setExercises([
      ...exercises,
      {
        repeticoes: 10,
        serie: 3,
        tipo: "KG",
        carga: 0,
        exercicio: {
          id: 0,
        },
      },
    ])
    // Inicializar estados para o novo exercício
    setOpenPopovers((prev) => ({ ...prev, [newIndex]: false }))
    setSearchValues((prev) => ({ ...prev, [newIndex]: "" }))
  }

  const removeExercise = (index: number) => {
    const updatedExercises = [...exercises]
    updatedExercises.splice(index, 1)
    setExercises(updatedExercises)

    // Limpar estados relacionados ao exercício removido
    const newOpenPopovers = { ...openPopovers }
    const newSearchValues = { ...searchValues }
    delete newOpenPopovers[index]
    delete newSearchValues[index]
    setOpenPopovers(newOpenPopovers)
    setSearchValues(newSearchValues)
  }

  const updateExercise = (
    index: number,
    field: "repeticoes" | "serie" | "tipo" | "carga" | "exercicioId",
    value: any,
  ) => {
    const updatedExercises = [...exercises]

    if (field === "exercicioId") {
      const exerciseId = Number(value)
      updatedExercises[index].exercicio.id = exerciseId

      const selectedExercise = availableExercises.find((ex) => ex.id === exerciseId)
      if (selectedExercise) {
        updatedExercises[index].exercicio.nome = selectedExercise.nome
        // Atualizar o valor de pesquisa com o nome do exercício selecionado
        setSearchValues((prev) => ({ ...prev, [index]: selectedExercise.nome }))
      }
    } else if (field === "repeticoes" || field === "serie" || field === "carga") {
      updatedExercises[index][field] = Number(value)
    } else if (field === "tipo") {
      updatedExercises[index][field] = value as ExerciseType
    }

    setExercises(updatedExercises)
  }

  const handleSubmit = async () => {
    if (!trainingName || exercises.length === 0) {
      showToast("Preencha o nome do treino e adicione pelo menos um exercício", "error")
      return
    }

    if (exercises.some((ex) => ex.exercicio.id === 0)) {
      showToast("Selecione um exercício para cada item", "error")
      return
    }

    setSubmitting(true)

    try {
      const payload = {
        id: isEditMode ? trainingId : undefined,
        nome: trainingName,
        idFicha: sheetId,
        exercicios: exercises,
        unidade: {
          id: getUnitId(),
        },
      }

      console.log("Saving training:", payload)

      showToast(isEditMode ? "Treino atualizado" : "Treino adicionado", "success")

      if (isEditMode) {
        await httpClientPut(`/fichas-treino`, { ...payload, inclusao: new Date() })
      } else {
        await httpClientPost(`/fichas-treino`, payload)
      }
      changeTab("workout-list")
      setParams({ traningId: "" } as any)
    } catch (error) {
      console.error("Error saving training:", error)
      showToast("Ocorreu um erro ao salvar o treino", "error")
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`
  }

  return data ? (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Informações da Ficha</CardTitle>
          <CardDescription>Detalhes da ficha de exercícios</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-5">
            <div>
              <Label className="font-bold">Ficha</Label>
              <p>{data?.nome}</p>
            </div>

            <div>
              <Label className="font-bold">Aluno</Label>
              <p>{data?.aluno.nome}</p>
            </div>

            <div>
              <Label className="font-bold">Início</Label>
              <p>{formatDate(data?.dataInicio)}</p>
            </div>

            <div>
              <Label className="font-bold">Fim</Label>
              <p>{formatDate(data?.dataFim)}</p>
            </div>

            <div>
              <Label className="font-bold">Frequência</Label>
              <p>{data?.frequencia}</p>
            </div>

            <div>
              <Label className="font-bold">Quantidade de treinos</Label>
              <p>{data?.numeroTreinos}</p>
            </div>

            <div className="col-span-full">
              <Label className="font-bold">Observação</Label>
              <p>{data?.observacao}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{isEditMode ? "Editar Treino" : "Adicionar Treino"}</CardTitle>
          <CardDescription>
            {isEditMode
              ? "Edite os detalhes e exercícios do treino"
              : "Preencha os detalhes e adicione exercícios ao treino"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <Label htmlFor="training-name">Nome do Treino</Label>
              <Input
                id="training-name"
                value={trainingName}
                onChange={(e) => setTrainingName(e.target.value)}
                placeholder="Ex: Treino A - Peito e Tríceps"
                className="w-full mt-1 placeholder:text-gray-400 placeholder:italic"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Exercícios</h3>
                <Button onClick={addExercise} variant="outline" size="sm" className="flex items-center gap-1">
                  <Plus className="h-4 w-4" /> Adicionar Exercício
                </Button>
              </div>

              {exercises.length === 0 && (
                <div className="text-center py-8 border rounded-md bg-muted/50">
                  <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">Nenhum exercício adicionado</p>
                  <Button onClick={addExercise} variant="link" className="mt-2">
                    Adicionar exercício
                  </Button>
                </div>
              )}

              <div className="space-y-3">
                {exercises.map((exercise, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 p-4 border rounded-md">
                    {/* Exercício com pesquisa */}
                    <div className="col-span-12 sm:col-span-4">
                      <Label htmlFor={`exercise-${index}`}>Exercício</Label>
                      <Popover
                        open={openPopovers[index] || false}
                        onOpenChange={(open) => setOpenPopovers((prev) => ({ ...prev, [index]: open }))}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openPopovers[index] || false}
                            className="w-full justify-between mt-1"
                          >
                            <span className="truncate">
                              {exercise.exercicio.id !== 0
                                ? availableExercises.find((ex) => ex.id === exercise.exercicio.id)?.nome ||
                                "Exercício não encontrado"
                                : "Selecione um exercício"}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput
                              placeholder="Pesquisar exercício..."
                              value={searchValues[index] || ""}
                              onValueChange={(value) => setSearchValues((prev) => ({ ...prev, [index]: value }))}
                            />
                            <CommandList>
                              <CommandEmpty>Nenhum exercício encontrado.</CommandEmpty>
                              <CommandGroup>
                                {getFilteredExercises(searchValues[index] || "").map((ex) => (
                                  <CommandItem
                                    key={ex.id}
                                    value={ex.nome}
                                    onSelect={() => {
                                      updateExercise(index, "exercicioId", ex.id.toString())
                                      setOpenPopovers((prev) => ({ ...prev, [index]: false }))
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        exercise.exercicio.id === ex.id ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    {ex.nome}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Séries */}
                    <div className="col-span-6 sm:col-span-2">
                      <Label htmlFor={`series-${index}`}>Séries</Label>
                      <Input
                        id={`series-${index}`}
                        type="number"
                        value={exercise.serie}
                        onChange={(e) => updateExercise(index, "serie", e.target.value)}
                        min={1}
                        className="mt-1"
                      />
                    </div>

                    {/* Repetições */}
                    <div className="col-span-6 sm:col-span-2">
                      <Label htmlFor={`reps-${index}`}>Repetições</Label>
                      <Input
                        id={`reps-${index}`}
                        type="number"
                        value={exercise.repeticoes}
                        onChange={(e) => updateExercise(index, "repeticoes", e.target.value)}
                        min={1}
                        className="mt-1"
                      />
                    </div>

                    {/* Tipo */}
                    <div className="col-span-6 sm:col-span-2">
                      <Label htmlFor={`type-${index}`}>Tipo</Label>
                      <Select value={exercise.tipo} onValueChange={(value) => updateExercise(index, "tipo", value)}>
                        <SelectTrigger id={`type-${index}`} className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="KG">KG</SelectItem>
                          <SelectItem value="PESO">PESO</SelectItem>
                          <SelectItem value="LIBRA">LIBRA</SelectItem>
                          <SelectItem value="PERCENTUAL">PERCENTUAL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Carga */}
                    <div className="col-span-5 sm:col-span-1">
                      <Label htmlFor={`weight-${index}`}>Carga</Label>
                      <Input
                        id={`weight-${index}`}
                        type="number"
                        value={exercise.carga}
                        onChange={(e) => updateExercise(index, "carga", e.target.value)}
                        min={0}
                        step={0.5}
                        className="mt-1"
                      />
                    </div>

                    {/* Velocidade de Execução */}
                    <div className="col-span-12 sm:col-span-3">
                      <Label htmlFor={`velocidadeExecucao-${index}`}>Velocidade de Execução</Label>
                      <Input
                        id={`velocidadeExecucao-${index}`}
                        value={exercise.velocidadeExecucao || ""}
                        onChange={(e) => {
                          const newExercises = [...exercises]
                          newExercises[index].velocidadeExecucao = e.target.value
                          setExercises(newExercises)
                        }}
                        placeholder="Ex: Lenta, Moderada"
                        className="mt-1 placeholder:text-gray-400 placeholder:italic"
                      />
                    </div>

                    {/* Tempo de Pausa */}
                    <div className="col-span-6 sm:col-span-2">
                      <Label htmlFor={`tempoPausaSeries-${index}`}>Pausa (s)</Label>
                      <Input
                        type="number"
                        id={`tempoPausaSeries-${index}`}
                        value={exercise.tempoPausaSeries ?? ""}
                        onChange={(e) => {
                          const newExercises = [...exercises]
                          newExercises[index].tempoPausaSeries = Number(e.target.value)
                          setExercises(newExercises)
                        }}
                        min={0}
                        placeholder="60"
                        className="mt-1"
                      />
                    </div>

                    {/* Observação */}
                    <div className="col-span-12 sm:col-span-6">
                      <Label htmlFor={`observacao-${index}`}>Observação</Label>
                      <Input
                        id={`observacao-${index}`}
                        value={exercise.observacao || ""}
                        onChange={(e) => {
                          const newExercises = [...exercises]
                          newExercises[index].observacao = e.target.value
                          setExercises(newExercises)
                        }}
                        placeholder="Observações adicionais"
                        className="mt-1 placeholder:text-gray-400 placeholder:italic"
                      />
                    </div>

                    {/* Remove Button */}
                    <div className="col-span-1 flex items-end">
                      <Button onClick={() => removeExercise(index)} variant="destructive" size="icon" className="mt-1">
                        <Trash className="h-4 w-4" />
                        <span className="sr-only">Remover exercício</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end align-middle gap-2 pt-4">
              <Button
                onClick={() => {
                  changeTab("workout-list")
                  setParams({ traningId: "" } as any)
                }}
                variant="outline"
                disabled={submitting}
              >
                Cancelar
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="col-span-2 bg-acelerafit-green-400 text-muted font-bold hover:bg-acelerafit-green-300 gap-1"
              >
                <Save className="h-4 w-4" />
                {submitting ? "Salvando..." : isEditMode ? "Atualizar Treino" : "Salvar Treino"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  ) : (
    <div className="flex items-center justify-center h-full">
      <p className="text-gray-500">Selecione uma ficha para cadastrar o treino.</p>
    </div>
  )
}
